/**
 * Environment Variable & AI Configuration Hooks
 * Provides React Query hooks for managing environment variables,
 * AI settings, and connection testing.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { environmentApi } from '../api/client';
import type {
  EnvironmentVariable,
  EnvironmentVariableListItem,
  EnvironmentVariableCreateRequest,
  EnvironmentVariableUpdateRequest,
  EnvVarStats,
  EnvVarAuditEntry,
  AIConfigSummary,
  AIConfigUpdateRequest,
  ConnectionTestRequest,
  ConnectionTestResult,
  EnvVarCategory,
} from '../types';

// ============ Query Key Constants ============

export const envQueryKeys = {
  list: (params?: Record<string, unknown>) =>
    ['environment-variables', params] as const,
  detail: (id: string) => ['environment-variables', id] as const,
  stats: ['environment-variables', 'stats'] as const,
  audit: (id: string) => ['environment-variables', id, 'audit'] as const,
  aiConfig: ['environment-variables', 'ai-config'] as const,
};

// ============ List & Detail Hooks ============

/** List environment variables with optional filters */
export function useEnvironmentVariables(params?: {
  category?: string;
  scope?: string;
  is_sensitive?: boolean;
  search?: string;
}, enabled = true) {
  return useQuery<EnvironmentVariableListItem[]>({
    queryKey: envQueryKeys.list(params),
    queryFn: () => environmentApi.list(params),
    enabled,
    staleTime: 30000,
  });
}

/** Get single environment variable detail */
export function useEnvironmentVariable(id: string, enabled = true) {
  return useQuery<EnvironmentVariable>({
    queryKey: envQueryKeys.detail(id),
    queryFn: () => environmentApi.get(id),
    enabled: enabled && !!id,
  });
}

/** Get environment variable statistics */
export function useEnvVarStats(enabled = true) {
  return useQuery<EnvVarStats>({
    queryKey: envQueryKeys.stats,
    queryFn: () => environmentApi.stats(),
    enabled,
    staleTime: 60000,
  });
}

/** Get audit log for a specific variable */
export function useEnvVarAudit(id: string, enabled = true) {
  return useQuery<EnvVarAuditEntry[]>({
    queryKey: envQueryKeys.audit(id),
    queryFn: () => environmentApi.audit(id),
    enabled: enabled && !!id,
  });
}

/** Get AI configuration summary */
export function useAIConfig(enabled = true) {
  return useQuery<AIConfigSummary>({
    queryKey: envQueryKeys.aiConfig,
    queryFn: () => environmentApi.aiConfig(),
    enabled,
    staleTime: 30000,
  });
}

// ============ Mutation Hooks ============

/** Create a new environment variable */
export function useCreateEnvVar() {
  const queryClient = useQueryClient();
  return useMutation<EnvironmentVariable, Error, EnvironmentVariableCreateRequest>({
    mutationFn: (data) => environmentApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-variables'] });
    },
  });
}

/** Update an environment variable */
export function useUpdateEnvVar() {
  const queryClient = useQueryClient();
  return useMutation<
    EnvironmentVariable,
    Error,
    { id: string; data: EnvironmentVariableUpdateRequest }
  >({
    mutationFn: ({ id, data }) => environmentApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['environment-variables'] });
      queryClient.invalidateQueries({
        queryKey: envQueryKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: envQueryKeys.aiConfig });
    },
  });
}

/** Delete an environment variable */
export function useDeleteEnvVar() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => environmentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-variables'] });
    },
  });
}

/** Reveal a sensitive variable's value */
export function useRevealEnvVar() {
  return useMutation<{ value: string }, Error, string>({
    mutationFn: (id) => environmentApi.reveal(id),
  });
}

/** Update AI configuration (bulk PATCH) */
export function useUpdateAIConfig() {
  const queryClient = useQueryClient();
  return useMutation<AIConfigSummary, Error, AIConfigUpdateRequest>({
    mutationFn: (data) => environmentApi.updateAIConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: envQueryKeys.aiConfig });
      queryClient.invalidateQueries({ queryKey: ['environment-variables'] });
    },
  });
}

/** Test a connection using a variable's value */
export function useTestConnection() {
  return useMutation<ConnectionTestResult, Error, ConnectionTestRequest>({
    mutationFn: (data) => environmentApi.testConnection(data),
  });
}

// ============ Helper Functions ============

/** Category display info */
export const ENV_CATEGORIES: Record<
  EnvVarCategory,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  ai: {
    label: 'AI / LLM',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50 border-purple-200',
    icon: 'Brain',
  },
  system: {
    label: 'System',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 border-blue-200',
    icon: 'Settings',
  },
  api_keys: {
    label: 'API Keys',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50 border-amber-200',
    icon: 'Key',
  },
  database: {
    label: 'Datenbank',
    color: 'text-green-700',
    bgColor: 'bg-green-50 border-green-200',
    icon: 'Database',
  },
  email: {
    label: 'E-Mail / SMTP',
    color: 'text-pink-700',
    bgColor: 'bg-pink-50 border-pink-200',
    icon: 'Mail',
  },
  integration: {
    label: 'Integration',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50 border-cyan-200',
    icon: 'Plug',
  },
  websocket: {
    label: 'WebSocket',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50 border-indigo-200',
    icon: 'Radio',
  },
  celery: {
    label: 'Task Queue',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50 border-orange-200',
    icon: 'Layers',
  },
  feature_flags: {
    label: 'Feature Flags',
    color: 'text-teal-700',
    bgColor: 'bg-teal-50 border-teal-200',
    icon: 'Flag',
  },
};

/** Get category info with fallback */
export function getCategoryInfo(category: EnvVarCategory) {
  return (
    ENV_CATEGORIES[category] ?? {
      label: category,
      color: 'text-gray-700',
      bgColor: 'bg-gray-50 border-gray-200',
      icon: 'Circle',
    }
  );
}

/** Auto-detect category from key name */
export function detectCategory(key: string): EnvVarCategory {
  const k = key.toUpperCase();
  if (
    k.startsWith('ANTHROPIC_') ||
    k.startsWith('CLAUDE_') ||
    k.startsWith('OPENAI_') ||
    k.startsWith('LLM_') ||
    k.startsWith('AI_')
  )
    return 'ai';
  if (k.startsWith('DATABASE_') || k.startsWith('DB_') || k.startsWith('POSTGRES'))
    return 'database';
  if (k.startsWith('EMAIL_') || k.startsWith('SMTP_'))
    return 'email';
  if (k.startsWith('CELERY_') || k.startsWith('REDIS_'))
    return 'celery';
  if (k.startsWith('WS_') || k.startsWith('WEBSOCKET_'))
    return 'websocket';
  if (k.includes('SECRET') || k.includes('TOKEN') || k.includes('API_KEY'))
    return 'api_keys';
  if (k.startsWith('INFOR_') || k.startsWith('ODATA_'))
    return 'integration';
  if (k.startsWith('FEATURE_') || k.startsWith('FF_'))
    return 'feature_flags';
  return 'system';
}

/** Auto-detect if a key is sensitive */
export function detectSensitive(key: string): boolean {
  const k = key.toUpperCase();
  const sensitivePatterns = [
    'PASSWORD',
    'SECRET',
    'KEY',
    'TOKEN',
    'AUTH',
    'CREDENTIAL',
    'PRIVATE',
    'CERT',
  ];
  return sensitivePatterns.some((p) => k.includes(p));
}
